import { getIntl } from 'utils/getTranslations';
import { isNewDesignCopy } from 'utils/newDesignCopy';
import { formatReportNumber } from '../reportCells';

// The new design writes month names and changes through its locale; classic keeps its own formatters.

const shortMonth = new Intl.DateTimeFormat('en-GB', { month: 'short' });
const longMonth = new Intl.DateTimeFormat('en-GB', { month: 'long' });

/** A `YYYY-MM-DD` report date as a local date, so the month does not shift with the time zone. */
export function parseReportDate(value: string): Date {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
}

/** `Jan`, or `Jan 2026` when the figures span more than one year. */
export function monthLabel(month: string, withYear: boolean): string {
    const date = parseReportDate(month);
    if (isNewDesignCopy()) {
        return getIntl().formatDate(date, withYear ? { month: 'short', year: 'numeric' } : { month: 'short' });
    }
    const name = shortMonth.format(date);
    return withYear ? `${name} ${date.getFullYear()}` : name;
}

/** `January 2026`, for a tooltip or a screen reader. */
export function fullMonthLabel(month: string): string {
    const date = parseReportDate(month);
    if (isNewDesignCopy()) {
        return getIntl().formatDate(date, { month: 'long', year: 'numeric' });
    }
    return `${longMonth.format(date)} ${date.getFullYear()}`;
}

/** Whether `months` (first days, `YYYY-MM-DD`) fall in more than one year. */
export function spansYears(months: string[]): boolean {
    return new Set(months.map((month) => month.slice(0, 4))).size > 1;
}

/** The names of the twelve months, January first, for a month filter's options. */
export function monthNames(): string[] {
    const name = (date: Date) =>
        isNewDesignCopy() ? getIntl().formatDate(date, { month: 'long' }) : longMonth.format(date);
    return Array.from({ length: 12 }, (_, index) => name(new Date(2000, index, 1)));
}

/** A whole number, as a bar's value label and a key figure show it. */
export function formatWhole(value: number | null | undefined): string {
    return formatReportNumber(value, 0);
}

/** A signed percent change, `+4%` in the new design, `+4%` or `−3%` in classic; empty when there is none. */
export function formatChange(value: number | null | undefined, decimals = 0): string {
    if (value == null) return '';
    if (isNewDesignCopy()) {
        return getIntl().formatNumber(value / 100, {
            style: 'percent',
            signDisplay: 'exceptZero',
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        });
    }
    const sign = value > 0 ? '+' : value < 0 ? '−' : '';
    return `${sign}${formatReportNumber(Math.abs(value), decimals)}%`;
}
